import Component from '@glimmer/component'
import RouteTemplate from 'ember-route-template'
import { cached } from '@glimmer/tracking'
import { context } from 'customer-flows/utils/core'
import { action } from '@ember/object'
import { service } from '@ember/service'
import didInsert from '@ember/render-modifiers/modifiers/did-insert'
import { scheduleOnce } from '@ember/runloop'
import { t } from 'ember-intl'
import { normalizeFlowBootError } from 'customer-flows/flow/boot-error'
import StaticFooter from 'customer-flows/components/booking/static-footer'

interface FlowErrorSignature {
  Args: {
    model: unknown
  }
}

class FlowErrorTemplate extends Component<FlowErrorSignature> {
  @service frameManager: FrameManager
  @service intentionalNavigation: IntentionalNavigation

  @context('account') availabilityBrandingConfig: AccountStore['availabilityBrandingConfig']

  @cached
  get error() {
    return normalizeFlowBootError(this.args.model)
  }

  get isBranded(): boolean {
    return Boolean(this.availabilityBrandingConfig)
  }

  @action
  onInsert() {
    this.frameManager.postMessage('hide-loading')
    this.frameManager.setEmbedHeight()
  }

  @action
  focusOnInsert(element: HTMLElement) {
    scheduleOnce('afterRender', this, () => {
      const shadowButton = element.shadowRoot?.querySelector('button')
      ;(shadowButton ?? element).focus({ preventScroll: true })
    })
  }

  @action
  onTryAgain() {
    this.intentionalNavigation.reload()
  }

  <template>
    <div data-testid='flow-error' class='flex h-full w-full flex-col' {{didInsert this.onInsert}}>
      {{#if this.isBranded}}
        <div data-testid='flow-error__header' class='z-10 w-full'>
          <il-page-header
            logo-url={{this.availabilityBrandingConfig.headerLogoUrl}}
            logo-label={{this.availabilityBrandingConfig.headerText}}
            header-style={{this.availabilityBrandingConfig.headerStyle}}
          />
        </div>
      {{/if}}

      <div
        data-testid='flow-error__content'
        class='flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center'
        role='alert'
      >
        <h1 data-testid='flow-error__heading' class='text-xbold-header-20 text-neutrals-350'>
          {{t 'flow-error.heading'}}
        </h1>

        <p data-testid='flow-error__message' class='text-reg-para-16 text-neutrals-400 max-sm:max-w-60'>
          {{#if this.error.retryable}}
            {{t 'flow-error.message-retryable'}}
          {{else}}
            {{t 'flow-error.message-permanent'}}
          {{/if}}
        </p>

        {{#if this.error.retryable}}
          <il-button
            {{! @glint-ignore: wait until we can't declare custom elements in glint }}
            onClick={{this.onTryAgain}}
            data-testid='flow-error__try-again'
            {{didInsert this.focusOnInsert}}
          >
            {{t 'flow-error.try-again'}}
          </il-button>
        {{/if}}
      </div>

      <StaticFooter @text={{t 'booking.footer.text' htmlSafe=true}} />
    </div>
  </template>
}

export default RouteTemplate(FlowErrorTemplate)
